// aiPanelFocus — opening, focusing and putting away the composer's focus
// panel (DESIGN-COMPOSER-FOCUS-POPOVER-CONTEXT-INSPECT-1; the panel is
// components/_simone/Suggestions.js, mounted in CanvasPromptTextarea's
// PromptBand).
//
// The panel PAINTS while the composer has focus (`_promptFocused`) and
// `root._aiPanel` does not say otherwise:
//   null         the resting rule — on focus, except while the assistant's
//                thread or drawer holds the answer (it steps aside for it)
//   'open'       asked for (the context pill, ↑ in an empty field): shown even
//                over the thread, until focus leaves or it is put away
//   'dismissed'  put away (Escape, a pick, a verb) — until the field gets
//                focus again
//
// Every function takes `this` = an element INSIDE the composer (the field,
// the pill, a panel row); the composer is found by its key through `lookup`.
import { focusFirstMenuRow, menuNodeVisible } from './menuKeyboard.js'

const _aipfComposerOf = (el) => {
  if (!el) return null
  if (el.key === 'CanvasPromptTextarea') return el
  return typeof el.lookup === 'function' ? el.lookup('CanvasPromptTextarea') : null
}

// The panel element of the composer `el` sits in, or null.
export const aiPanelOf = (el) => {
  const composer = _aipfComposerOf(el)
  const band = composer && composer.PromptBand
  return (band && band.SimoneSuggestions) || null
}

// Is the panel on screen right now (painted, not merely mounted)?
export const aiPanelShown = (el) => {
  const panel = aiPanelOf(el)
  return !!(panel && panel.node && menuNodeVisible(panel.node))
}

// OPEN the panel and move focus into it: 'scope' → the chosen context chip,
// anything else → the last suggestion row (the one nearest the field).
export const aiPanelOpen = function aiPanelOpen(target) {
  const el = this
  const root = el && el.state && el.state.root
  if (!root || typeof root.update !== 'function') return false
  root.update({ _aiPanel: 'open', _promptFocused: true }, { preventFetch: true })
  const panel = aiPanelOf(el)
  if (!panel) return true
  const scope = target === 'scope'
  focusFirstMenuRow(() => panel.node, {
    selector: scope ? '[role="radio"][aria-checked="true"]' : '[role="menuitem"]',
    last: !scope,
    when: (node) => menuNodeVisible(node)
  })
  return true
}

// PUT AWAY the panel: focus returns to the field (first — its focus edge
// resets a put-away panel, so the flag is written after it), the panel stays
// down until the field is focused again.
export const aiPanelDismiss = function aiPanelDismiss() {
  const el = this
  const root = el && el.state && el.state.root
  if (!root || typeof root.update !== 'function') return false
  const composer = _aipfComposerOf(el)
  const field = composer && composer.Textarea && composer.Textarea.node
  const doc = typeof document !== 'undefined' ? document : null
  if (field && typeof field.focus === 'function' && (!doc || doc.activeElement !== field)) {
    field.focus()
  }
  root.update({ _aiPanel: 'dismissed' }, { preventFetch: true })
  return true
}
